import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, put, del } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { Tag } from "@/types";

export type { Tag };

export interface TaggedContent {
  documents: import("@/types").Document[];
  notes: import("@/types").Note[];
  quotes: import("@/types").Quote[];
}

export function extractTagsFromContent(content: string): string[] {
  const matches = content.match(/#([A-Za-z][\w-]*)/g);
  if (!matches) return [];
  return [...new Set(matches.map((tag) => tag.substring(1).toLowerCase()))];
}

export function useTags() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: tags = [],
    isLoading,
    error,
    isFetched,
  } = useQuery({
    queryKey: ["/api/tags"],
    queryFn: () => get<Tag[]>("/tags"),
  });

  const createTagMutation = useMutation({
    mutationFn: (
      newTag: Pick<Tag, "name"> & Partial<Pick<Tag, "color" | "description">>,
    ) => post<Tag>("/tags", newTag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      toast({ title: "Tag created" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ id, ...data }: Partial<Tag> & { id: number }) =>
      put<Tag>(`/tags/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      toast({ title: "Tag updated" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: number) => del(`/tags/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      toast({ title: "Tag deleted" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const highlightTags = useCallback(
    (content: string, onTagClick?: (tag: string) => void): React.ReactNode => {
      const parts = content.split(/(#[A-Za-z][\w-]*)/g);

      return parts.map((part, index) => {
        if (part.startsWith("#")) {
          const tagName = part.substring(1);
          const tag = tags.find(
            (t) => t.name.toLowerCase() === tagName.toLowerCase(),
          );
          const style = {
            backgroundColor: tag?.color || "#e2e8f0",
            borderRadius: "4px",
            padding: "2px 4px",
            cursor: onTagClick ? "pointer" : "default",
          };

          return (
            <span
              key={index}
              style={style}
              onClick={() => onTagClick?.(tagName)}
              role={onTagClick ? "button" : undefined}
              tabIndex={onTagClick ? 0 : undefined}
            >
              {part}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      });
    },
    [tags],
  );

  const generateTagColor = useCallback(() => {
    const colors = [
      "#f87171",
      "#fb923c",
      "#fbbf24",
      "#a3e635",
      "#4ade80",
      "#2dd4bf",
      "#38bdf8",
      "#60a5fa",
      "#818cf8",
      "#a78bfa",
      "#e879f9",
      "#f472b6",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);

  return {
    tags,
    isLoading,
    isFetched,
    error,
    createTag: createTagMutation.mutate,
    createTagAsync: createTagMutation.mutateAsync,
    updateTag: updateTagMutation.mutate,
    deleteTag: deleteTagMutation.mutate,
    extractTags: extractTagsFromContent,
    highlightTags,
    generateTagColor,
  };
}

export function useDocumentTags(documentId: number | null | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const enabled = typeof documentId === "number" && documentId > 0;

  const {
    data: tags = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["/api/documents", documentId, "tags"],
    queryFn: () => get<Tag[]>(`/documents/${documentId}/tags`),
    enabled,
  });

  const attachMutation = useMutation({
    mutationFn: (tagId: number) =>
      post(`/documents/${documentId}/tags`, { tag_id: tagId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/documents", documentId, "tags"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add tag",
        variant: "destructive",
      });
    },
  });

  const detachMutation = useMutation({
    mutationFn: (tagId: number) =>
      del(`/documents/${documentId}/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/documents", documentId, "tags"],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove tag",
        variant: "destructive",
      });
    },
  });

  const syncHashtags = useCallback(
    async (content: string) => {
      if (!enabled) return;
      await post(`/documents/${documentId}/tags/sync-hashtags`, { content });
      await queryClient.invalidateQueries({
        queryKey: ["/api/documents", documentId, "tags"],
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
    },
    [documentId, enabled, queryClient],
  );

  return {
    tags,
    isLoading,
    refetch,
    attachTag: attachMutation.mutate,
    attachTagAsync: attachMutation.mutateAsync,
    detachTag: detachMutation.mutate,
    syncHashtags,
  };
}

export function useTaggedContent(tagId: number) {
  return useQuery({
    queryKey: [`/api/tags/${tagId}/content`],
    queryFn: () => get<TaggedContent>(`/tags/${tagId}/content`),
    enabled: !!tagId,
  });
}
